/**
 * Profil du pilote (phase C) : crédits, objets achetés, chargement de chaque châssis, statistiques.
 * Logique pure (testée) et conservation dans le navigateur. Les règles d'économie sont dans data/equipment.json.
 */
import { z } from 'zod';
import { shipIds, slotIds, type GameData, type ShipId, type SlotId } from '../data';
import { itemFits, sanitizeLoadout, type Loadout } from '../sim/loadout';
import type { Outcome } from '../sim/telemetry/telemetry';

export interface PilotStats {
  sorties: number;
  victories: number;
  bestScore: number;
  creditsEarned: number;
}

export interface PilotProfile {
  version: 1;
  credits: number;
  /** Objets payants achetés (les objets gratuits sont toujours disponibles). */
  owned: string[];
  loadouts: Record<ShipId, Loadout>;
  stats: PilotStats;
}

export interface Reward {
  /** Crédits issus du score, après le facteur d'issue. */
  fromScore: number;
  bonus: number;
  total: number;
}

const KEY = 'space-arena.profile.v1';

const storedSchema = z.object({
  version: z.literal(1),
  credits: z.number().int().min(0),
  owned: z.array(z.string()),
  loadouts: z.record(z.string(), z.record(z.string(), z.string())),
  stats: z.object({ sorties: z.number(), victories: z.number(), bestScore: z.number(), creditsEarned: z.number() }),
});

export function newProfile(data: GameData): PilotProfile {
  return {
    version: 1,
    credits: data.equipment.economy.startingCredits,
    owned: [],
    loadouts: Object.fromEntries(shipIds.map((id) => [id, sanitizeLoadout(data, id, undefined)])) as Record<ShipId, Loadout>,
    stats: { sorties: 0, victories: 0, bestScore: 0, creditsEarned: 0 },
  };
}

export function owns(profile: PilotProfile, data: GameData, id: string): boolean {
  const item = data.equipment.items[id];
  return !!item && (item.price === 0 || profile.owned.includes(id));
}

/** Relit un profil enregistré en écartant ce que les réglages actuels ne connaissent plus (objet retiré, renommé). */
export function restoreProfile(data: GameData, raw: unknown): PilotProfile {
  const parsed = storedSchema.safeParse(raw);
  if (!parsed.success) return newProfile(data);
  const stored = parsed.data;
  const profile: PilotProfile = {
    version: 1,
    credits: stored.credits,
    owned: [...new Set(stored.owned.filter((id) => data.equipment.items[id]))],
    loadouts: newProfile(data).loadouts,
    stats: { ...stored.stats },
  };
  for (const ship of shipIds) {
    const requested = { ...stored.loadouts[ship] };
    for (const slot of slotIds) if (requested[slot] && !owns(profile, data, requested[slot]!)) delete requested[slot];
    profile.loadouts[ship] = sanitizeLoadout(data, ship, requested);
  }
  return profile;
}

export function loadProfile(data: GameData): PilotProfile {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? restoreProfile(data, JSON.parse(raw)) : newProfile(data);
  } catch {
    return newProfile(data);
  }
}

export function saveProfile(profile: PilotProfile): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(profile));
  } catch {
    // Stockage indisponible : la progression vaut pour cette visite seulement.
  }
}

/** Crédits d'une mission : score × taux × facteur d'issue, plus la prime de victoire du scénario. Sans mission : rien. */
export function missionReward(data: GameData, scenarioId: string | null, outcome: Outcome, score: number): Reward {
  const e = data.equipment.economy;
  const scenario = scenarioId ? data.missions.scenarios[scenarioId] : undefined;
  if (!scenario) return { fromScore: 0, bonus: 0, total: 0 };
  const factor = outcome === 'victory' ? 1 : outcome === 'defeat' ? e.defeatFactor : e.abandonFactor;
  const fromScore = Math.round(score * e.scoreRate * factor);
  const bonus = outcome === 'victory' ? scenario.victoryBonus : 0;
  return { fromScore, bonus, total: fromScore + bonus };
}

/** Crédite une mission terminée et met à jour les statistiques. */
export function awardSortie(profile: PilotProfile, data: GameData, scenarioId: string | null, outcome: Outcome, score: number): Reward {
  const reward = missionReward(data, scenarioId, outcome, score);
  profile.credits += reward.total;
  profile.stats.creditsEarned += reward.total;
  if (scenarioId && data.missions.scenarios[scenarioId]) {
    profile.stats.sorties++;
    if (outcome === 'victory') profile.stats.victories++;
    profile.stats.bestScore = Math.max(profile.stats.bestScore, score);
  }
  return reward;
}

export type PurchaseResult = 'bought' | 'owned' | 'funds' | 'unknown';

export function buyItem(profile: PilotProfile, data: GameData, id: string): PurchaseResult {
  const item = data.equipment.items[id];
  if (!item) return 'unknown';
  if (owns(profile, data, id)) return 'owned';
  if (profile.credits < item.price) return 'funds';
  profile.credits -= item.price;
  profile.owned.push(id);
  return 'bought';
}

/** Monte un objet possédé et compatible ; faux sinon. */
export function equipItem(profile: PilotProfile, data: GameData, ship: ShipId, slot: SlotId, id: string): boolean {
  if (!owns(profile, data, id) || !itemFits(data, ship, slot, id)) return false;
  profile.loadouts[ship] = { ...profile.loadouts[ship], [slot]: id };
  return true;
}
